import { getFunctions } from "firebase/functions";
import { tracedCallable } from "../../tracedCallable";
import {
  findAccount,
  findContact,
  findFund,
} from "../../../locations/transactions/validation";
import { amountToCentsInt } from "../../general_util";

/**
 * Import journal entries via backend function with coordinated BigQuery sync
 *
 * This function calls the backend importJournalEntriesBatch Cloud Function,
 * which handles:
 * 1. Creating a sync job for tracking
 * 2. Writing transactions to Firestore with skipBigQuerySync flag
 * 3. Batch syncing to BigQuery via Storage Write API
 * 4. Cleaning up tracking documents
 *
 * Benefits over direct Firestore writes:
 * - Atomic BigQuery sync (all or nothing)
 * - No streaming insert rate limits
 * - Better performance for large batches
 * - Proper tracking and error recovery
 */
export const handleImport = async ({
  setSubmitting,
  setNumberToImport,
  initialImportData,
  user,
  setImportedTrans,
  setImportSuccess,
  setImportError,
  db: _db, // Not used directly - we call the Cloud Function instead
  accounts,
  contacts,
  funds,
  org,
}) => {
  setSubmitting(true);
  setNumberToImport(initialImportData.length);

  try {
    // Transform the data to the format expected by the backend
    const journalEntries = initialImportData.map((entry) => ({
      date: entry.date,
      checkNumber: entry.checkNumber ? entry.checkNumber : false,
      memo: entry.memo,
      userMadeChange: user.uid,
      addTimestamp: true,
      imported: true,
      journalEntry: false,
      lines: entry.lines.map((line, lineIndex) => {
        const account = findAccount(line.account, accounts);
        const fund = findFund(line.fund, funds);
        const contact = findContact(entry.contact, contacts);

        if (!account) {
          throw new Error(
            `Line ${lineIndex + 1}: Account "${line.account}" not found. Please fix in the error handling step.`,
          );
        }
        if (!fund) {
          throw new Error(
            `Line ${lineIndex + 1}: Fund "${line.fund}" not found. Please fix in the error handling step.`,
          );
        }

        return {
          account: account.id,
          contact: contact?.id || null,
          fund: fund.id,
          comment: line.comment,
          amount: amountToCentsInt(line.amount),
          sign: line.sign,
        };
      }),
    }));

    // Call the backend function
    const functions = getFunctions();
    const importJournalEntriesBatch = tracedCallable(
      functions,
      "importJournalEntriesBatch",
    );

    console.log(
      `📤 Calling importJournalEntriesBatch for ${journalEntries.length} entries...`,
    );

    const result = await importJournalEntriesBatch({
      orgId: org,
      journalEntries,
      importFileName: `frontend_import_${new Date().toISOString()}`,
    });

    const data = result.data;

    if (data.success) {
      console.log(
        `✅ Import successful: ${data.bigQuerySynced} transactions synced to BigQuery (${data.bigQueryRows} rows)`,
      );
      setImportedTrans(initialImportData);
      setImportSuccess(true);
    } else {
      console.warn(
        `⚠️ Partial import: ${data.bigQuerySynced}/${data.firestoreWritten} synced, ${data.failedCount} failed`,
      );
      // Still mark as partially successful if some transactions were imported
      if (data.firestoreWritten > 0) {
        setImportedTrans(initialImportData.slice(0, data.firestoreWritten));
      }
      setImportSuccess(false);
    }
  } catch (error) {
    console.error("Error importing journal entries:", error);
    setImportSuccess(false);
    setSubmitting(false);
    if (setImportError) {
      setImportError(error.message);
    }
  }
};
